import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar as CalendarIcon, Plus, Trash2, Clock, Users, Monitor, Building2, ToggleLeft, ToggleRight } from 'lucide-react';
import { useAvailability, TimeSlotConfig } from '../contexts/AvailabilityContext';
import { Calendar } from '../components/Calendar';
export function AdminAvailability() {
  const {
    availability,
    addSlot,
    removeSlot,
    toggleSlotActive
  } = useAvailability();
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [newSlot, setNewSlot] = useState({
    time: '08:00',
    capacity: 15,
    modality: 'presencial' as 'presencial' | 'virtual',
    price: 1
  });
  // Format date to YYYY-MM-DD
  const formatDateKey = (date: Date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };
  const dateKey = selectedDate ? formatDateKey(selectedDate) : null;
  // Admin sees ALL slots (active + inactive)
  const daySlots: TimeSlotConfig[] = dateKey ? availability[dateKey] || [] : [];
  // Convert 24h time to 12h format for display
  const formatTime12h = (time24: string) => {
    const [h, m] = time24.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${String(m).padStart(2, '0')} ${ampm}`;
  };
  const handleAddSlot = (e: React.FormEvent) => {
    e.preventDefault();
    if (!dateKey) return;
    addSlot(dateKey, {
      time: formatTime12h(newSlot.time),
      capacity: newSlot.capacity,
      modality: newSlot.modality,
      price: newSlot.price
    });
    setNewSlot(prev => ({
      ...prev,
      time: '08:00'
    }));
  };
  // Get all dates that have ANY slots (active or inactive) for calendar dots
  const availableDates = new Set(Object.keys(availability));
  const activeCount = daySlots.filter(s => s.active).length;
  const inactiveCount = daySlots.filter(s => !s.active).length;
  return <div className="space-y-6" data-id="element-0">
      <div data-id="element-1">
        <h1 className="font-display text-3xl font-bold text-earth mb-2" data-id="element-2">
          Disponibilidad
        </h1>
        <p className="text-stone" data-id="element-3">
          Gestiona los días y horarios en los que estás disponible para dar
          clases. Activa o desactiva horarios con un clic.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.5fr] gap-8" data-id="element-4">
        {/* Calendar Column */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-white rounded-2xl border border-mist shadow-sm p-6" data-id="element-5">
          <h2 className="font-bold text-earth text-lg mb-4 flex items-center gap-2" data-id="element-6">
            <CalendarIcon size={20} className="text-sage" data-id="element-7" />
            Selecciona un día
          </h2>

          <Calendar selectedDate={selectedDate} onSelectDate={setSelectedDate} availableDates={availableDates} allDaysSelectable={true} data-id="element-8" />

          <div className="mt-4 p-4 bg-sand/30 rounded-xl space-y-2" data-id="element-9">
            <p className="text-sm text-stone flex items-center gap-2" data-id="element-10">
              <span className="w-2 h-2 rounded-full bg-sage inline-block" data-id="element-11"></span>
              Días con horarios configurados
            </p>
            <p className="text-xs text-stone/60" data-id="element-12">
              Haz clic en un día para ver y gestionar sus horarios
            </p>
          </div>
        </motion.div>

        {/* Slots Column */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="bg-white rounded-2xl border border-mist shadow-sm flex flex-col h-full" data-id="element-13">
          <div className="p-6 border-b border-mist bg-sand/30" data-id="element-14">
            <h2 className="font-bold text-earth text-lg" data-id="element-15">
              {selectedDate ? `Horarios para el ${selectedDate.toLocaleDateString('es-MX', {
              weekday: 'long',
              day: 'numeric',
              month: 'long'
            })}` : 'Selecciona un día para gestionar horarios'}
            </h2>
            {selectedDate && daySlots.length > 0 && <div className="flex items-center gap-4 mt-2" data-id="element-16">
                <span className="text-xs text-sage font-medium flex items-center gap-1.5" data-id="element-17">
                  <span className="w-2 h-2 rounded-full bg-sage" data-id="element-18"></span>
                  {activeCount} activo{activeCount !== 1 ? 's' : ''}
                </span>
                <span className="text-xs text-stone/50 font-medium flex items-center gap-1.5" data-id="element-19">
                  <span className="w-2 h-2 rounded-full bg-stone/30" data-id="element-20"></span>
                  {inactiveCount} inactivo{inactiveCount !== 1 ? 's' : ''}
                </span>
              </div>}
          </div>

          {selectedDate ? <div className="p-6 flex-1 flex flex-col" data-id="element-21">
              {/* Add new slot form */}
              <form onSubmit={handleAddSlot} className="bg-sand/30 p-5 rounded-xl border border-mist mb-8" data-id="element-22">
                <h3 className="text-sm font-semibold text-earth mb-4" data-id="element-23">
                  Agregar nuevo horario
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-24">
                  <div data-id="element-25">
                    <label className="block text-xs font-semibold text-stone uppercase tracking-wider mb-1.5" data-id="element-26">
                      Hora
                    </label>
                    <input type="time" required value={newSlot.time} onChange={e => setNewSlot({
                  ...newSlot,
                  time: e.target.value
                })} className="w-full p-2.5 border border-mist rounded-lg text-sm outline-none focus:border-sage focus:ring-2 focus:ring-sage/20 bg-white" data-id="element-27" />
                  </div>
                  <div data-id="element-28">
                    <label className="block text-xs font-semibold text-stone uppercase tracking-wider mb-1.5" data-id="element-29">
                      Modalidad
                    </label>
                    <select value={newSlot.modality} onChange={e => setNewSlot({
                  ...newSlot,
                  modality: e.target.value as any
                })} className="w-full p-2.5 border border-mist rounded-lg text-sm outline-none focus:border-sage focus:ring-2 focus:ring-sage/20 bg-white" data-id="element-30">
                      <option value="presencial" data-id="element-31">Presencial</option>
                      <option value="virtual" data-id="element-32">Virtual (Zoom)</option>
                    </select>
                  </div>
                  <div data-id="element-33">
                    <label className="block text-xs font-semibold text-stone uppercase tracking-wider mb-1.5" data-id="element-34">
                      Capacidad
                    </label>
                    <input type="number" min="1" required value={newSlot.capacity} onChange={e => setNewSlot({
                  ...newSlot,
                  capacity: parseInt(e.target.value)
                })} className="w-full p-2.5 border border-mist rounded-lg text-sm outline-none focus:border-sage focus:ring-2 focus:ring-sage/20 bg-white" data-id="element-35" />
                  </div>
                  <div data-id="element-36">
                    <label className="block text-xs font-semibold text-stone uppercase tracking-wider mb-1.5" data-id="element-37">
                      Precio (USD)
                    </label>
                    <input type="number" min="0" required value={newSlot.price} onChange={e => setNewSlot({
                  ...newSlot,
                  price: parseInt(e.target.value)
                })} className="w-full p-2.5 border border-mist rounded-lg text-sm outline-none focus:border-sage focus:ring-2 focus:ring-sage/20 bg-white" data-id="element-38" />
                  </div>
                </div>

                <button type="submit" className="w-full flex items-center justify-center gap-2 bg-sage text-white py-2.5 rounded-lg font-medium hover:bg-sage/90 transition-colors" data-id="element-39">
                  <Plus size={18} data-id="element-40" />
                  Agregar Horario
                </button>
              </form>

              {/* List of slots */}
              <div className="flex-1" data-id="element-41">
                <h3 className="text-sm font-semibold text-earth mb-4" data-id="element-42">
                  Horarios programados ({daySlots.length})
                </h3>

                {daySlots.length === 0 ? <div className="text-center py-10 text-stone/60" data-id="element-43">
                    <Clock size={40} className="mx-auto mb-3 opacity-50" data-id="element-44" />
                    <p data-id="element-45">No hay horarios programados para este día.</p>
                  </div> : <div className="space-y-3" data-id="element-46">
                    <AnimatePresence data-id="element-47">
                      {daySlots.map(slot => <motion.div key={slot.id} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  x: -20
                }} transition={{
                  duration: 0.2
                }} className={`flex items-center justify-between p-4 border rounded-xl transition-all ${slot.active ? 'border-sage/40 bg-sage/5 hover:border-sage/60' : 'border-mist bg-stone/5 opacity-60 hover:opacity-80'}`} data-id="element-48">
                          <div className="flex items-center gap-4" data-id="element-49">
                            {/* Active/Inactive toggle */}
                            <button onClick={() => toggleSlotActive(dateKey!, slot.id)} className={`transition-colors ${slot.active ? 'text-sage hover:text-sage/70' : 'text-stone/40 hover:text-stone/60'}`} title={slot.active ? 'Desactivar horario' : 'Activar horario'} data-id="element-50">
                              {slot.active ? <ToggleRight size={28} data-id="element-51" /> : <ToggleLeft size={28} data-id="element-52" />}
                            </button>

                            <div className={`font-display text-xl font-bold w-24 ${slot.active ? 'text-earth' : 'text-stone/50'}`} data-id="element-53">
                              {slot.time}
                            </div>
                            <div className="flex items-center gap-3" data-id="element-54">
                              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${slot.active ? slot.modality === 'presencial' ? 'bg-presencial-light text-sage' : 'bg-virtual-light text-virtual' : 'bg-stone/10 text-stone/50'}`} data-id="element-55">
                                {slot.modality === 'presencial' ? <Building2 size={12} data-id="element-56" /> : <Monitor size={12} data-id="element-57" />}
                                {slot.modality === 'presencial' ? 'Presencial' : 'Virtual'}
                              </span>
                              <span className={`text-sm flex items-center gap-1.5 ${slot.active ? 'text-stone' : 'text-stone/40'}`} data-id="element-58">
                                <Users size={14} data-id="element-59" />
                                {slot.capacity} lugares
                              </span>
                              <span className={`text-sm font-medium ${slot.active ? 'text-earth' : 'text-stone/40'}`} data-id="element-60">
                                ${slot.price}
                              </span>
                            </div>
                          </div>
                          <button onClick={() => removeSlot(dateKey!, slot.id)} className="p-2 text-stone hover:text-error hover:bg-error/10 rounded-lg transition-colors" title="Eliminar horario" data-id="element-61">
                            <Trash2 size={18} data-id="element-62" />
                          </button>
                        </motion.div>)}
                    </AnimatePresence>
                  </div>}
              </div>
            </div> : <div className="p-12 text-center text-stone/60 flex-1 flex flex-col items-center justify-center" data-id="element-63">
              <CalendarIcon size={48} className="mb-4 opacity-20" data-id="element-64" />
              <p data-id="element-65">
                Selecciona un día en el calendario para ver y editar sus
                horarios.
              </p>
            </div>}
        </motion.div>
      </div>
    </div>;
}